import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import B2BLayout from '../../../layouts/b2b'
import {
  deletePaymentVariant,
  getPaymentVariants,
  getPlatformModules,
  updatePlatformModule,
  upsertPaymentVariant,
} from '../api'

const { Title, Text } = Typography

function fmtRub(val: unknown) {
  if (val == null || val === '') return '—'
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(Number(val))
}

type ModuleRow = {
  code: string
  name: string
  is_active: boolean
  is_required: boolean
  price_monthly?: number
}

type VariantPrice = {
  module_code: string
  module_name?: string
  price_monthly: number
  base_total: number
  price_total: number
  price_monthly_effective: number
}

type PaymentVariant = {
  term_months: number
  priority: number
  discount_percent: number
  discount_badge?: string | null
  prices: VariantPrice[]
}

export default function PlatformModulesPage() {
  const [rows, setRows] = useState<ModuleRow[]>([])
  const [variants, setVariants] = useState<PaymentVariant[]>([])
  const [loading, setLoading] = useState(false)
  const [savingCode, setSavingCode] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingTerm, setEditingTerm] = useState<number | null>(null)
  const [savingVariant, setSavingVariant] = useState(false)
  const [form] = Form.useForm()
  const savingLockRef = useRef(false)
  const pendingPriceRef = useRef<Record<string, number>>({})

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent)
    if (!silent) setLoading(true)
    try {
      const [modulesResult, variantsResult] = await Promise.all([
        getPlatformModules(),
        getPaymentVariants(),
      ])
      setRows(modulesResult?.data || [])
      setVariants(variantsResult?.data || [])
    } catch (e: any) {
      message.error(e?.message || 'Не удалось загрузить данные')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const activeModules = useMemo(() => rows.filter(r => r.is_active || r.is_required), [rows])

  const saveField = async (code: string, patch: Record<string, unknown>) => {
    if (savingLockRef.current) return
    savingLockRef.current = true
    setSavingCode(code)
    try {
      await updatePlatformModule(code, patch)
      setRows(prev => prev.map(r => (r.code === code ? { ...r, ...patch } : r)))
      message.success('Сохранено')
      await load({ silent: true })
    } catch (e: any) {
      message.error(e?.message || 'Ошибка сохранения')
      await load({ silent: true })
    } finally {
      savingLockRef.current = false
      setSavingCode(null)
    }
  }

  const openCreate = () => {
    setEditingTerm(null)
    form.setFieldsValue({
      term_months: undefined,
      discount_percent: 0,
      priority: variants.length ? Math.max(...variants.map(v => v.priority)) + 1 : 0,
    })
    setModalOpen(true)
  }

  const openEdit = (variant: PaymentVariant) => {
    setEditingTerm(variant.term_months)
    form.setFieldsValue({
      term_months: variant.term_months,
      discount_percent: Number(variant.discount_percent) || 0,
      priority: variant.priority,
    })
    setModalOpen(true)
  }

  const onSaveVariant = async () => {
    try {
      const values = await form.validateFields()
      const termMonths = Number(values.term_months)

      if (
        editingTerm != null &&
        editingTerm !== termMonths &&
        variants.some(v => v.term_months === termMonths)
      ) {
        message.error('Вариант с таким периодом уже существует')
        return
      }

      setSavingVariant(true)

      if (editingTerm != null && editingTerm !== termMonths) {
        await deletePaymentVariant(editingTerm)
      }

      await upsertPaymentVariant({
        term_months: termMonths,
        discount_percent: Number(values.discount_percent) || 0,
        priority: Number(values.priority) || 0,
      })
      message.success('Вариант оплаты сохранён')
      setModalOpen(false)
      await load({ silent: true })
    } catch (e: any) {
      if (e?.errorFields) return
      message.error(e?.message || 'Ошибка сохранения варианта')
    } finally {
      setSavingVariant(false)
    }
  }

  const onDeleteVariant = async (termMonths: number) => {
    try {
      await deletePaymentVariant(termMonths)
      message.success('Вариант удалён')
      await load({ silent: true })
    } catch (e: any) {
      message.error(e?.message || 'Не удалось удалить')
    }
  }

  const breadcrumbs = [
    { href: '/admin/dealers/domains', title: 'Поддомены дилеров' },
    { href: '/admin/dealers/modules', title: 'Модули' },
  ]

  const watchedTerm = Form.useWatch('term_months', form)
  const watchedDiscount = Form.useWatch('discount_percent', form)

  const previewRows = useMemo(() => {
    const term = Number(watchedTerm) || 0
    const discount = Math.max(0, Math.min(100, Number(watchedDiscount) || 0))
    if (term < 1) return []
    return activeModules.map(m => {
      const baseMonthly = Number(m.price_monthly) || 0
      const baseTotal = Math.round(baseMonthly * term * 100) / 100
      const priceTotal = Math.round(baseTotal * (1 - discount / 100) * 100) / 100
      return {
        code: m.code,
        name: m.name,
        baseMonthly,
        baseTotal,
        priceTotal,
        effectiveMonthly: term ? Math.round((priceTotal / term) * 100) / 100 : 0,
      }
    })
  }, [activeModules, watchedTerm, watchedDiscount])

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle="Модули платформы">
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        <Title level={4} style={{ margin: 0 }}>
          Каталог модулей
        </Title>
        <Text type="secondary">
          Базовая цена фиксирована за месяц. Скидка применяется к периоду оплаты.
        </Text>
        <Table
          rowKey="code"
          loading={loading}
          dataSource={rows}
          pagination={false}
          columns={[
            {
              title: 'Название',
              dataIndex: 'name',
              key: 'name',
              render: (name: string, row: ModuleRow) => (
                <Space>
                  {name}
                  {row.is_required && <Tag color="blue">Обязательный</Tag>}
                </Space>
              ),
            },
            {
              title: 'Цена, ₽/мес',
              dataIndex: 'price_monthly',
              key: 'price_monthly',
              width: 180,
              render: (val: number, row: ModuleRow) => (
                <InputNumber
                  min={0}
                  step={100}
                  value={Number(val) || 0}
                  disabled={!row.code || savingCode === row.code}
                  style={{ width: '100%' }}
                  formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  parser={v => Number(String(v).replace(/\s/g, '')) as any}
                  onChange={v => {
                    if (!row.code || v == null) return
                    const next = Number(v)
                    if (Number.isFinite(next) && next >= 0) {
                      pendingPriceRef.current[row.code] = next
                    }
                  }}
                  onBlur={() => {
                    if (!row.code || savingLockRef.current) return
                    const next = pendingPriceRef.current[row.code]
                    delete pendingPriceRef.current[row.code]
                    if (next == null || !Number.isFinite(next) || next < 0) return
                    if (next === Number(val || 0)) return
                    saveField(row.code, { price_monthly: next })
                  }}
                />
              ),
            },
            {
              title: 'Активен в каталоге',
              dataIndex: 'is_active',
              key: 'is_active',
              width: 160,
              render: (val: boolean, row: ModuleRow) => (
                <Switch
                  checked={val}
                  disabled={!row.code || row.is_required || savingCode === row.code}
                  onChange={checked => {
                    if (!row.code) return
                    saveField(row.code, { is_active: checked })
                  }}
                />
              ),
            },
          ]}
        />

        <Space style={{ width: '100%', justifyContent: 'space-between' }} align="center">
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Периоды оплаты
            </Title>
            <Text type="secondary">
              Скидка % общая для всех модулей. Итог = база × период × (1 − скидка%)
            </Text>
          </div>
          <Button type="primary" onClick={openCreate}>
            Добавить период
          </Button>
        </Space>

        <Table
          rowKey="term_months"
          loading={loading}
          dataSource={variants}
          pagination={false}
          columns={[
            {
              title: 'Период',
              dataIndex: 'term_months',
              key: 'term_months',
              width: 120,
              render: (v: number) => `${v} мес.`,
            },
            {
              title: 'Скидка',
              dataIndex: 'discount_percent',
              key: 'discount_percent',
              width: 120,
              render: (v: number, row: PaymentVariant) =>
                row.discount_badge || (Number(v) > 0 ? `−${v}%` : '—'),
            },
            {
              title: 'Приоритет',
              dataIndex: 'priority',
              key: 'priority',
              width: 100,
            },
            {
              title: 'Модули (итог за период)',
              key: 'prices',
              render: (_: unknown, row: PaymentVariant) => (
                <dl
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'max-content auto',
                    columnGap: 16,
                    rowGap: 6,
                    margin: 0,
                  }}
                >
                  {row.prices.map(p => (
                    <React.Fragment key={p.module_code}>
                      <dt style={{ margin: 0 }}>{p.module_name || p.module_code}</dt>
                      <dd style={{ display: 'inline-block', marginLeft: 15 }}>
                        {Number(row.discount_percent) > 0 ? (
                          <>
                            <Text delete type="secondary">
                              {fmtRub(p.base_total)}
                            </Text>{' '}
                            <Text strong>{fmtRub(p.price_total)}</Text>
                          </>
                        ) : (
                          <Text strong>{fmtRub(p.price_total)}</Text>
                        )}
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                          {fmtRub(p.price_monthly_effective)}/мес
                        </Text>
                      </dd>
                    </React.Fragment>
                  ))}
                </dl>
              ),
            },
            {
              title: '',
              key: 'actions',
              width: 180,
              render: (_: unknown, row: PaymentVariant) => (
                <Space>
                  <Button size="small" onClick={() => openEdit(row)}>
                    Изменить
                  </Button>
                  <Popconfirm
                    title="Удалить период оплаты?"
                    okText="Удалить"
                    cancelText="Отмена"
                    onConfirm={() => onDeleteVariant(row.term_months)}
                  >
                    <Button size="small" danger>
                      Удалить
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />

        <Button onClick={() => load()} loading={loading}>
          Обновить
        </Button>
      </Space>

      <Modal
        open={modalOpen}
        title={editingTerm != null ? `Период: ${editingTerm} мес.` : 'Добавить период оплаты'}
        onCancel={() => setModalOpen(false)}
        onOk={onSaveVariant}
        confirmLoading={savingVariant}
        okText="Сохранить"
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="term_months"
            label="Период (месяцев)"
            rules={[
              { required: true, message: 'Укажите период' },
              {
                validator: async (_, value) => {
                  const n = Number(value)
                  if (!Number.isInteger(n) || n < 1) {
                    throw new Error('Период должен быть целым числом ≥ 1')
                  }
                },
              },
            ]}
          >
            <InputNumber
              min={1}
              step={1}
              style={{ width: '100%' }}
              disabled={editingTerm != null}
            />
          </Form.Item>
          <Form.Item
            name="discount_percent"
            label="Скидка, %"
            rules={[
              { required: true, message: 'Укажите скидку' },
              {
                validator: async (_, value) => {
                  const n = Number(value)
                  if (!Number.isFinite(n) || n < 0 || n > 100) {
                    throw new Error('Скидка от 0 до 100')
                  }
                },
              },
            ]}
            extra="0% — без скидки. Применяется ко всем модулям"
          >
            <InputNumber min={0} max={100} step={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="priority"
            label="Приоритет"
            rules={[{ required: true, message: 'Укажите приоритет' }]}
            extra="Меньше значение — выше в списке тарифов"
          >
            <InputNumber step={1} style={{ width: '100%' }} />
          </Form.Item>

          {previewRows.length > 0 && (
            <>
              <Title level={5}>Превью расчёта</Title>
              <Table
                size="small"
                pagination={false}
                rowKey="code"
                dataSource={previewRows}
                columns={[
                  { title: 'Модуль', dataIndex: 'name', key: 'name' },
                  {
                    title: 'База /мес',
                    key: 'baseMonthly',
                    render: (_: unknown, r: (typeof previewRows)[0]) => fmtRub(r.baseMonthly),
                  },
                  {
                    title: 'За период',
                    key: 'total',
                    render: (_: unknown, r: (typeof previewRows)[0]) =>
                      Number(watchedDiscount) > 0 ? (
                        <>
                          <Text delete type="secondary">
                            {fmtRub(r.baseTotal)}
                          </Text>{' '}
                          <Text strong>{fmtRub(r.priceTotal)}</Text>
                        </>
                      ) : (
                        fmtRub(r.priceTotal)
                      ),
                  },
                ]}
              />
            </>
          )}
        </Form>
      </Modal>
    </B2BLayout>
  )
}

export { fmtRub }
